import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, ArrowRight, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import logo from "@/assets/img/logo.webp";

const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <footer className="bg-accent text-accent-foreground">
      {/* CTA Banner */}
      <div className="bg-primary relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="container-max px-4 py-12 md:py-16 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl text-primary-foreground font-bold tracking-tight">
                Ready for a Truly Clean Home?
              </h3>
              <p className="text-primary-foreground/80 text-sm mt-1">
                Free estimates across Tampa Bay — open Monday – Saturday, 8 AM to 6 PM.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={openQuoteForm}
                className="gap-2 rounded-xl font-bold"
              >
                Get Started <ArrowRight className="h-5 w-5" />
              </Button>
              <a href="tel:9785645018">
                <Button
                  variant="secondary"
                  size="lg"
                  className="gap-2 rounded-xl font-bold bg-white text-primary hover:bg-white/90"
                >
                  <Phone className="h-4 w-4" /> Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer content */}
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-4 inline-flex items-center justify-center rounded-2xl bg-white p-3 shadow-premium">
              <img src={logo} alt="JR Pro Cleaning Services" width={160} height={127} className="h-12 w-auto" loading="lazy" decoding="async" />
            </div>
            <p className="text-accent-foreground/75 text-sm leading-relaxed">
              JR Pro Cleaning Services LLC — professional house, deep, and commercial cleaning
              proudly serving Tampa and the entire bay area.
            </p>
            <div className="mt-4 bg-secondary/10 border border-secondary/20 rounded-xl px-4 py-3">
              <p className="text-white font-semibold text-sm">
                ✨ Free estimates — call today!
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: "Services", to: "/services" },
                { label: "Locations", to: "/locations" },
                { label: "Gallery", to: "/gallery" },
                { label: "Blog", to: "/blog" },
                { label: "About", to: "/about" },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="footer-link-underline text-accent-foreground/75 hover:text-white text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Contact
            </h4>
            <ul className="space-y-3 text-sm text-accent-foreground/80">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary" />
                <a href="tel:9785645018" className="hover:text-white transition-colors">(978) 564-5018</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary" />
                <a href="tel:9783357648" className="hover:text-white transition-colors">(978) 335-7648</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-secondary" />
                <a href="mailto:jrprocleanersservices@gmail.com" className="hover:text-white transition-colors break-all">jrprocleanersservices@gmail.com</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <span>5830 Memorial Hwy, Apt 402<br />Tampa, FL</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Hours
            </h4>
            <ul className="space-y-2 text-sm text-accent-foreground/80">
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0 text-secondary" />
                Monday – Saturday
              </li>
              <li className="pl-6">8:00 AM – 6:00 PM</li>
              <li className="pl-6 text-xs text-accent-foreground/65">Sunday: Closed</li>
            </ul>
            <div className="mt-4 space-y-1">
              <p className="text-accent-foreground/65 text-xs">Residential · Commercial · Airbnb</p>
              <p className="text-accent-foreground/65 text-xs">Serving Tampa Bay & surrounding areas</p>
            </div>
            <div className="mt-3 flex items-center gap-2 text-accent-foreground/80 text-sm">
              <Shield className="h-4 w-4 shrink-0 text-secondary" />
              <span>Fully Insured & Certified</span>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-accent-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-accent-foreground/60 text-xs">
            © {new Date().getFullYear()} JR Pro Cleaning Services LLC. All rights reserved.
          </p>
          <span className="text-accent-foreground/60 text-xs">House Cleaning · Deep Cleaning · Commercial Cleaning</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
